import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Card, Col, Flex, Input, Row, Select, Space, Spin, Tag, Tree, Typography } from 'antd'
import type { DataNode } from 'antd/es/tree'
import { PlusOutlined, TagsOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { useAuth } from '@/shared/auth/useAuth'
import { getCurrencyCode } from '@/shared/lib/format'
import { useLocalListState } from '@/shared/lib/localList'
import { EmptyState } from '@/shared/ui/EmptyState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RowActions } from '@/shared/ui/table/RowActions'
import { useOperationRunner } from '@/shared/ui/useOperationRunner'
import { OrgNodeDetails } from '@/features/org/OrgNodeDetails'
import { OrgNodeFormModal } from '@/features/org/OrgNodeFormModal'
import { OrgNodeMoveModal } from '@/features/org/OrgNodeMoveModal'
import { OrgSetupGuide } from '@/features/org/OrgSetupGuide'
import {
  buildOrgTree,
  fetchOrgNodeTypes,
  fetchOrgTree,
  kindName,
  ORG_NODE_STATES,
  orgTypeName,
  setOrgNodeState,
  type OrgNode,
  type OrgNodeState,
  type OrgTreeNode,
} from '@/features/org/orgApi'
import {
  ORG_STATE_COLOR,
  TOP_LEVEL,
  canHoldChildren,
  defaultLocation,
  isOfficeNode,
  officesFirst,
} from '@/features/org/orgSetup'

interface FormTarget {
  node: OrgNode | null
  location: string | null
  kind: string | null
}

function toOrgNodeState(value: string | null): OrgNodeState | null {
  return ORG_NODE_STATES.find((state) => state === value) ?? null
}

export function OrgStructurePage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const currency = user?.tenant.currency ?? getCurrencyCode()

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const list = useLocalListState({ defaultSort: null })
  const search = list.search
  const kindFilter = list.filter('kind')
  const stateFilter = toOrgNodeState(list.filter('state'))
  const [expanded, setExpanded] = useState<string[] | null>(null)

  const filterFingerprint = `${search}|${kindFilter ?? ''}|${stateFilter ?? ''}`
  useEffect(() => {
    setExpanded(null)
  }, [filterFingerprint])

  const [formOpen, setFormOpen] = useState(false)
  const [formTarget, setFormTarget] = useState<FormTarget>({ node: null, location: null, kind: null })
  const [moveOpen, setMoveOpen] = useState(false)

  const tree = useQuery({
    queryKey: queryKeys.org.tree,
    queryFn: ({ signal }) => fetchOrgTree(signal),
  })

  const types = useQuery({
    queryKey: queryKeys.org.types,
    queryFn: fetchOrgNodeTypes,
    staleTime: 5 * 60_000,
  })

  const nodes = useMemo(() => tree.data?.nodes ?? [], [tree.data])
  const typeList = useMemo(() => types.data ?? [], [types.data])
  const hasOffices = useMemo(() => nodes.some((node) => isOfficeNode(node, typeList)), [nodes, typeList])

  const runner = useOperationRunner([queryKeys.org.all])

  const selected = useMemo(
    () => nodes.find((node) => node.id === selectedId) ?? null,
    [nodes, selectedId],
  )

  const filterActive = search.trim().length > 0 || kindFilter !== null || stateFilter !== null
  const hideClosed = stateFilter === null && search.trim().length === 0

  const matches = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return (node: OrgNode) =>
      (needle.length === 0 ||
        node.displayName.toLowerCase().includes(needle) ||
        node.code.toLowerCase().includes(needle)) &&
      (kindFilter === null || node.kind === kindFilter) &&
      (stateFilter === null || node.state === stateFilter) &&
      (!hideClosed || node.state !== 'CLOSED')
  }, [search, kindFilter, stateFilter, hideClosed])

  const matched = useMemo<ReadonlySet<string>>(
    () => new Set(nodes.filter(matches).map((node) => node.id)),
    [nodes, matches],
  )

  const visible = useMemo<ReadonlySet<string>>(() => {
    const byId = new Map(nodes.map((node) => [node.id, node]))
    const result = new Set<string>(matched)

    for (const id of matched) {
      let parentId = byId.get(id)?.parentId ?? null
      while (parentId !== null && !result.has(parentId)) {
        result.add(parentId)
        parentId = byId.get(parentId)?.parentId ?? null
      }
    }
    return result
  }, [nodes, matched])

  const openCreate = useCallback(
    (start: OrgNode | null, kind: string | null = null) => {
      setFormTarget({
        node: null,
        location: hasOffices ? defaultLocation(nodes, typeList, start) : TOP_LEVEL,
        kind,
      })
      setFormOpen(true)
    },
    [hasOffices, nodes, typeList],
  )

  const openEdit = useCallback((node: OrgNode) => {
    setSelectedId(node.id)
    setFormTarget({ node, location: null, kind: null })
    setFormOpen(true)
  }, [])

  const treeData = useMemo<DataNode[]>(() => {
    const convert = (node: OrgTreeNode): DataNode | null => {
      if (!visible.has(node.id)) return null
      const children = officesFirst(node.children, typeList)
        .map(convert)
        .filter((child): child is DataNode => child !== null)
      const dim = !matched.has(node.id)

      return {
        key: node.id,
        title: (
          <Space size={6} wrap>
            <Typography.Text strong={!dim} type={dim ? 'secondary' : undefined}>
              {node.displayName}
            </Typography.Text>
            <Tag style={{ marginInlineEnd: 0 }}>{kindName(typeList, node.kind, t)}</Tag>
            {node.state === 'ACTIVE' ? null : (
              <Tag color={ORG_STATE_COLOR[node.state]} style={{ marginInlineEnd: 0 }}>
                {t(`orgNodeState.${node.state}`)}
              </Tag>
            )}
            <span onClick={(e) => e.stopPropagation()}>
              <RowActions
                items={[
                  {
                    key: 'edit',
                    label: t('common.edit'),
                    onClick: () => openEdit(node),
                    permission: 'org.node.write',
                  },
                  {
                    key: 'addChild',
                    label: t('org.addInside'),
                    onClick: () => {
                      setSelectedId(node.id)
                      openCreate(node)
                    },
                    permission: 'org.node.write',
                    hidden: !canHoldChildren(node, typeList),
                  },
                  {
                    key: 'move',
                    label: t('org.move.button'),
                    onClick: () => {
                      setSelectedId(node.id)
                      setMoveOpen(true)
                    },
                    permission: 'org.node.write',
                    danger: true,
                    divider: true,
                  },
                  {
                    key: 'toggleState',
                    label:
                      node.state === 'ACTIVE' ? t('org.node.suspend') : t('org.node.activate'),
                    onClick: () => {
                      setSelectedId(node.id)
                      void runner.run(() =>
                        setOrgNodeState(node.id, node.state === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE'),
                      )
                    },
                    permission: 'org.node.write',
                    hidden: node.state === 'CLOSED',
                  },
                ]}
              />
            </span>
          </Space>
        ),
        children,
      }
    }

    return officesFirst(buildOrgTree(nodes), typeList)
      .map(convert)
      .filter((node): node is DataNode => node !== null)
  }, [nodes, typeList, visible, matched, t, runner, openCreate, openEdit])

  const autoExpanded = useMemo(
    () => nodes.filter((node) => visible.has(node.id)).map((node) => node.id),
    [nodes, visible],
  )
  const expandedKeys = expanded ?? autoExpanded

  const listedCount = hideClosed
    ? nodes.filter((node) => node.state !== 'CLOSED').length
    : nodes.length

  return (
    <>
      <PageHeader
        title={t('nav.orgStructure')}
        description={t('org.structureIntro')}
        extra={
          <>
            <Can permission="org.type.write">
              <Link to="/org/node-types">
                <Button type="text" icon={<TagsOutlined />}>
                  {t('nav.orgNodeTypes')}
                </Button>
              </Link>
            </Can>
            <Can permission="org.node.write">
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate(selected)}>
                {hasOffices ? t('org.add') : t('org.createHeadOffice')}
              </Button>
            </Can>
          </>
        }
      />

      {tree.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(tree.error, t)}
        />
      ) : null}

      {tree.data?.truncated ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('org.truncated', { shown: nodes.length, total: tree.data.total })}
        />
      ) : null}

      {tree.isPending ? <Spin /> : null}

      {tree.isSuccess && !hasOffices ? <OrgSetupGuide onStart={() => openCreate(null)} /> : null}

      {nodes.length > 0 ? (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={14}>
            <Card title={t('org.treeTitle', { count: listedCount })}>
              <Flex wrap gap="small" align="center" style={{ marginBottom: 12 }}>
                <Input.Search
                  allowClear
                  value={list.searchText}
                  onChange={(e) => list.setSearchText(e.target.value)}
                  placeholder={t('org.searchPlaceholder')}
                  style={{ width: 220, maxWidth: '100%' }}
                />
                <Select<string>
                  allowClear
                  value={kindFilter}
                  onChange={(value) => list.setFilter('kind', value ?? null)}
                  placeholder={t('org.filterKind')}
                  style={{ width: 160 }}
                  loading={types.isPending}
                  options={typeList.map((type) => ({ value: type.code, label: orgTypeName(type, t) }))}
                />
                <Select<string>
                  allowClear
                  value={stateFilter}
                  onChange={(value) => list.setFilter('state', value ?? null)}
                  placeholder={t('org.filterState')}
                  style={{ width: 140 }}
                  options={ORG_NODE_STATES.map((state) => ({
                    value: state,
                    label: t(`orgNodeState.${state}`),
                  }))}
                />
                {list.narrowed ? (
                  <Button type="link" size="small" onClick={list.reset}>
                    {t('dataTable.clearFilters')}
                  </Button>
                ) : null}
              </Flex>

              {treeData.length === 0 ? (
                <EmptyState
                  variant="no-data"
                  title={t('org.noMatches')}
                  description={t('org.noMatchesHint')}
                  {...(filterActive
                    ? { actionLabel: t('dataTable.clearFilters'), onAction: list.reset }
                    : {})}
                />
              ) : (
                <>
                  <Space size="small" wrap style={{ marginBottom: 8 }}>
                    <Button size="small" onClick={() => setExpanded([])}>
                      {t('org.collapseAll')}
                    </Button>
                    <Button size="small" onClick={() => setExpanded(nodes.map((node) => node.id))}>
                      {t('org.expandAll')}
                    </Button>
                    {filterActive ? (
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {t('org.filterNote', { count: matched.size })}
                      </Typography.Text>
                    ) : null}
                  </Space>
                  <Tree
                    showLine
                    blockNode
                    treeData={treeData}
                    expandedKeys={expandedKeys}
                    onExpand={(keys) => setExpanded(keys.map(String))}
                    selectedKeys={selectedId === null ? [] : [selectedId]}
                    onSelect={(keys) => setSelectedId(keys.length === 0 ? null : String(keys[0]))}
                  />
                </>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={10}>
            {selected === null ? (
              <Card>
                <EmptyState
                  variant="no-data"
                  title={t('org.selectHint')}
                  description={t('org.selectHintBody')}
                />
              </Card>
            ) : (
              <OrgNodeDetails
                node={selected}
                nodes={nodes}
                types={typeList}
                currency={currency}
                runner={runner}
                onEdit={() => openEdit(selected)}
                onMove={() => setMoveOpen(true)}
                onAdd={(kind) => openCreate(selected, kind)}
              />
            )}
          </Col>
        </Row>
      ) : null}

      <OrgNodeFormModal
        open={formOpen}
        node={formTarget.node}
        location={formTarget.location}
        presetKind={formTarget.kind}
        nodes={nodes}
        types={typeList}
        onClose={() => setFormOpen(false)}
      />

      {selected !== null ? (
        <OrgNodeMoveModal
          key={selected.id}
          open={moveOpen}
          node={selected}
          nodes={nodes}
          types={typeList}
          onClose={() => setMoveOpen(false)}
        />
      ) : null}
    </>
  )
}
